/**
 * Debug logging utility
 * Only logs when:
 * - In development mode (NODE_ENV=development or import.meta.env.DEV)
 * - OR when ?debug=true URL parameter is present
 * - OR when localStorage.debug is set to 'true'
 */

// Check for URL parameter ?debug=true (browser only)
const hasDebugParam = typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('debug') === 'true';

// Check for localStorage debug flag (persists across page loads)
const hasLocalStorageDebug = typeof window !== 'undefined' &&
  localStorage.getItem('debug') === 'true';

// Check for explicit logging override via environment
const hasEnvOverride = typeof process !== 'undefined' && process.env.FORCE_LOGGING === 'true';

// Detect if we're in a server context (SSR) where logging might be unwanted
const isServerContext = typeof window === 'undefined' && typeof process !== 'undefined';

// Check if in development mode
const isDevMode = typeof process !== 'undefined'
  ? process.env.NODE_ENV === 'development'
  : import.meta.env?.DEV ?? false;

// Enable logging if: debug param, localStorage flag, env override, or dev mode
const shouldLog = hasDebugParam || hasLocalStorageDebug || hasEnvOverride ||
  (isDevMode && !isServerContext);

// Persist debug flag if URL param is present (so it persists across navigation)
if (hasDebugParam && typeof window !== 'undefined') {
  localStorage.setItem('debug', 'true');
}

export const logger = {
  log: (...args: unknown[]) => {
    if (shouldLog) console.log('[WL]', ...args);
  },
  info: (...args: unknown[]) => {
    if (shouldLog) console.info('[WL]', ...args);
  },
  warn: (...args: unknown[]) => {
    if (shouldLog) console.warn('[WL]', ...args);
  },
  error: (...args: unknown[]) => {
    if (shouldLog) console.error('[WL]', ...args);
  },
  debug: (...args: unknown[]) => {
    if (shouldLog) console.debug('[WL]', ...args);
  },
  // Always log critical errors (even in production without debug flag)
  critical: (...args: unknown[]) => {
    console.error('[WL CRITICAL]', ...args);
  },
  // Check if logging is enabled
  isEnabled: () => shouldLog,
};
